import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/hvm";

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const ratingFor = (kj) => {
  if (kj < 650)
    return {
      label: "M30 / 48 km/h class",
      color: "text-[#22C55E]",
      product: "HySecurity StrongArm M30 or TiSO M30 bollard line",
      slug: "strongarm-m30",
    };
  if (kj < 1850)
    return {
      label: "M50 / 80 km/h class",
      color: "text-[#F59E0B]",
      product: "TiSO RB 33X shallow road blocker (ISO 22343 • M50)",
      slug: "cyclope-shallow-road-blocker-rb-33x",
    };
  return {
    label: "Beyond M50 — engineered solution",
    color: "text-[#EF4444]",
    product: "Automatic Systems BL M50 (2.0 MJ) — talk to us",
    slug: "bl-m50-vertical-lift-beam-barrier",
  };
};

const ThreatCalc = ({ threat, setThreat }) => {
  const { mass, speed } = threat;
  const kj = Math.round(0.5 * mass * 1000 * Math.pow(speed / 3.6, 2) / 1000);
  const rating = ratingFor(kj);

  const update = (m, s) => {
    const k = Math.round(0.5 * m * 1000 * Math.pow(s / 3.6, 2) / 1000);
    const r = ratingFor(k);
    setThreat({ mass: m, speed: s, kj: k, band: r.label, product: r.product, touched: true });
  };

  return (
    <div
      data-testid="threat-calculator"
      className="bg-[#131927]/90 backdrop-blur-md border border-[#26334D] clip-notch p-6 sm:p-7 tracing-beam"
    >
      <p className="font-tech text-[10px] tracking-[0.3em] uppercase text-[#F4A031] mb-1">
        Threat energy calculator
      </p>
      <p className="font-grotesk font-bold text-lg text-white mb-5">What are you stopping?</p>

      <div className="space-y-5">
        <div>
          <div className="flex justify-between font-tech text-xs tracking-widest uppercase text-[#94A3B8] mb-2">
            <span>Vehicle mass</span>
            <span data-testid="calc-mass-value" className="text-white">{mass.toFixed(1)} t</span>
          </div>
          <input
            type="range" min="1.5" max="7.5" step="0.1" value={mass}
            onChange={(e) => update(parseFloat(e.target.value), speed)}
            data-testid="calc-mass-slider"
            className="w-full accent-[#F4A031]"
          />
        </div>
        <div>
          <div className="flex justify-between font-tech text-xs tracking-widest uppercase text-[#94A3B8] mb-2">
            <span>Impact speed</span>
            <span data-testid="calc-speed-value" className="text-white">{speed} km/h</span>
          </div>
          <input
            type="range" min="20" max="90" step="5" value={speed}
            onChange={(e) => update(mass, parseInt(e.target.value))}
            data-testid="calc-speed-slider"
            className="w-full accent-[#F4A031]"
          />
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-[#26334D]">
        <p className="font-tech text-[10px] tracking-[0.3em] uppercase text-[#64748B]">Kinetic energy</p>
        <p data-testid="calc-energy-value" className="font-display font-bold text-5xl text-white leading-none mt-1">
          {kj.toLocaleString()} <span className="text-xl text-[#94A3B8]">kJ</span>
        </p>
        <p data-testid="calc-rating-band" className={`font-tech text-xs tracking-widest uppercase mt-3 ${rating.color}`}>
          → {rating.label}
        </p>
        <div className="mt-4 bg-[#0B0F17] border border-[#26334D] clip-notch-sm px-4 py-3">
          <p className="font-tech text-[10px] tracking-[0.3em] uppercase text-[#64748B]">Recommended product</p>
          <a
            href={`#product-${rating.slug}`}
            data-testid="calc-product-link"
            className="font-grotesk font-semibold text-sm text-white mt-1.5 block hover:text-[#F4A031] transition-colors underline decoration-[#384B70] underline-offset-4"
          >
            {rating.product} ↓
          </a>
        </div>
      </div>
    </div>
  );
};

export const Hero = ({ threat, setThreat }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden noise-overlay">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.hero}
          alt="TiSO Cyclope mobile hostile vehicle mitigation barriers protecting a city plaza"
          width="800"
          height="798"
          fetchPriority="high"
          className="w-full h-[120%] object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F17]/70 via-[#0B0F17]/55 to-[#0B0F17]" />
        <div className="absolute inset-0 tactical-grid opacity-60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <MaskLine delay={0.1}>
              <span className="inline-flex items-center gap-2.5 font-tech text-[11px] tracking-[0.3em] uppercase text-[#F4A031] border border-[#F4A031]/30 bg-[#F4A031]/5 px-3 py-1.5 clip-notch-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F4A031] pulse-dot" />
                Access X Oceania — HVM Division
              </span>
            </MaskLine>

            <h1 className="font-display font-bold uppercase leading-[0.92] tracking-tight mt-7 text-5xl sm:text-7xl lg:text-8xl">
              <MaskLine delay={0.25}>Protecting</MaskLine>
              <MaskLine delay={0.4}>people.</MaskLine>
              <MaskLine delay={0.55}>Protecting</MaskLine>
              <MaskLine delay={0.7}>
                <span className="text-[#F4A031]">places.</span>
              </MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.7 }}
              className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-xl mt-7"
            >
              We protect people and the places they gather — with certified bollards, road
              blockers, wedge barriers and crash-rated fencing, specified and supplied across
              Oceania, and installed by certified partners in your area.
              70+ product lines. Six leading brands. One scoping conversation.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.7 }}
              className="flex flex-wrap items-center gap-4 mt-9"
            >
              <a
                href="#assessment"
                data-testid="hero-cta-assessment"
                className="group inline-flex items-center gap-3 bg-[#F4A031] hover:bg-[#D98A1F] text-[#0B0F17] font-grotesk font-bold px-7 py-4 clip-notch-sm transition-colors"
              >
                Request a site assessment
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#standards"
                data-testid="hero-cta-standards"
                className="inline-flex items-center gap-2.5 border border-[#384B70] hover:border-[#F4A031] text-white font-grotesk font-medium px-7 py-4 clip-notch-sm transition-colors"
              >
                <ShieldCheck size={17} className="text-[#F4A031]" />
                Decode the standards
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.45, duration: 0.8 }}
              className="flex flex-wrap gap-x-8 gap-y-3 mt-10 font-tech text-[11px] tracking-[0.2em] uppercase text-[#64748B]"
            >
              <span data-testid="hero-stat-lines"><span className="text-[#F4A031]">70+</span> product lines</span>
              <span data-testid="hero-stat-brands"><span className="text-[#F4A031]">6</span> leading brands</span>
              <span data-testid="hero-stat-penetration"><span className="text-[#F4A031]">0.0 m</span> penetration options</span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <ThreatCalc threat={threat} setThreat={setThreat} />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
